// @ts-nocheck
import buttonShapes from "@insite/mobius/Button/buttonShapes";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import buttonTypes from "@insite/mobius/Button/buttonTypes";
import hoverAnimations from "@insite/mobius/Button/hoverAnimations";
// import { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button/Props"; // Starter Custom Code
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import get from "@insite/mobius/utilities/get";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
// import * as React from "react"; // Store Custom Code
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { applyStyleModifiers } from "styled-components-modifiers";

/* Starter Custom Code */
import LoadingSpinner, { LoadingSpinnerProps, LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@store/MobiusOverrides/Button/Props";
import useExistingRef from "@toolkit/Framework/Common/Hooks/useExistingRef";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, useRef } from "react";
/* Starter Custom Code */

export type ButtonSizeVariants = keyof typeof buttonSizeVariants;
// Store Custom Code
export type ButtonVariants = "primary" | "secondary" | "tertiary" | "primaryDark" | "secondaryDark";

export interface ButtonIconPresentationProps extends IconPresentationProps {}

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
        // this is needed because styled-components is confused
        disabled: boolean;
    };

// this should be BWT but typescript is not happy with "Type instantiation is excessively deep and possibly infinite." and nothing else was working
const ButtonWrapper = styled.button<any>`
    cursor: pointer;
    font-family: inherit;
    min-height: ${({ _sizeVariant, _size }) => _size || get(buttonSizeVariants, [_sizeVariant, "height"])}px;
    line-height: 1;
    backface-visibility: hidden;
    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
    word-break: keep-all;
    ${({ shadow, theme }) =>
        shadow &&
        `
        box-shadow: ${get(theme, "shadows.1")};
    `}
    padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};
    ${applyStyleModifiers(buttonShapes, "_shape")}
    ${applyStyleModifiers(buttonTypes, "buttonType")}
    ${applyStyleModifiers(hoverAnimations, "hoverAnimation")}
    &:disabled {
        cursor: not-allowed;
    }
    ${ButtonIcon} {
        position: relative;
        top: 0.125em;
    }
    // bunzl custom code - change to focus visible to not outline on click.
    &:focus-visible {
        outline-offset: 1px;
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
    }
    &::-moz-focus-inner {
        border: none;
    }
    /* Starter Custom Code */
    ${({ isLoading }) => {
        if (isLoading) {
            return css`
                > *:not(${LoadingSpinnerStyle}) {
                    visibility: hidden;
                }
            `;
        }
    }}
    /* Starter Custom Code */
    ${injectCss}
`;

const omitKeys = ["color", "shape", "size", "sizeVariant"] as const;
/* Store Custom Code - Start */
type ButtonContextProps = ThemeProps<BaseTheme>;
type ButtonComponentProps = Omit<React.HTMLAttributes<HTMLButtonElement>, "translate"> &
    ButtonProps &
    ButtonPresentationProps &
    ButtonContextProps &
    HasDisablerContext;
/* Store Custom Code - End */

const Button: FC<ButtonComponentProps /* Store Custom Code */> = props => {
    const {
        children,
        css: buttonCss,
        disable,
        disabled,
        forwardAs,
        icon,
        typographyProps,
        theme,
        variant = "primary",
        mergeCss,
        isFocusAfterMount,
        /* Starter Custom Code */
        spinnerProps: baseSpinnerProps,
        isLoading,
        /* Starter Custom Code */
        ref,
        ...otherProps
    } = props;
    const position = icon?.position;
    const src = icon?.src;
    const localRef = useRef<HTMLButtonElement | null>(null);
    const buttonRef = ref || localRef;
    let variantProps: ButtonPresentationProps = {};
    if (variant) {
        // Store Custom Code
        variantProps = theme.button[variant as keyof typeof theme.button] || {};
    }
    const { applyProp, applyStyledProp, spreadProps } = applyPropBuilder(props, {
        component: "button",
        // Store Custom Code - needed because there is no way to modify the base theme button types
        // but we need to be able to pass in other variants as props.
        propKey: variant as keyof typeof theme.button,
    });
    const sizeVariant = applyProp("sizeVariant", props.sizeVariant) as keyof typeof buttonSizeVariants;
    const size = applyProp("size", null) as number | null;
    // Store Custom Code
    const resolvedMergeCss = mergeCss ?? variantProps?.mergeCss ?? false;
    /* Starter Custom Code */
    const spinnerProps = React.useMemo(() => {
        return mergeProps<LoadingSpinnerProps>(
            {
                size: buttonSizeVariants[sizeVariant].height / 2,
                css: css`
                    position: absolute;
                `,
            },
            spreadProps("spinnerProps"),
        );
    }, [baseSpinnerProps, sizeVariant]);

    const as = forwardAs || (props.href ? "a" : undefined);
    const type = !as || as === "button" ? "button" : undefined;

    React.useEffect(() => {
        if (isFocusAfterMount) {
            ref?.current?.focus();
        }
    }, []);
    const typographySpreadProps: TypographyProps = spreadProps("typographyProps"); // Store Custom Code - to avoid spreading props in the return which causes too many re-renders
    return (
        <ButtonWrapper
            {...omitMultiple(variantProps, omitKeys)}
            ref={buttonRef}
            as={as} // Starter Custom Code
            css={applyStyledProp("css", resolvedMergeCss)}
            _color={applyProp("color", "primary")}
            _shape={applyProp("shape", "rectangle")}
            _sizeVariant={sizeVariant}
            _size={size}
            // Because disabled doesn't accept undefined
            // eslint-disable-next-line no-unneeded-ternary
            disabled={disable || disabled ? true : false}
            /* Starter Custom Code - Start */
            isLoading={isLoading}
            hoverTextColor={applyProp("hoverTextColor")}
            type={type}
            /* Starter Custom Code - End */
            {...omitMultiple(otherProps, omitKeys)}
        >
            {/* Store Custom Code - Start */}
            {icon?.both || position === "left" ? (
                <ButtonIcon
                    src={src}
                    size={buttonSizeVariants[sizeVariant].icon}
                    css={css`
                        margin-right: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                    `}
                />
            ) : null}
            {typeof children === "string" ? (
                <Typography
                    size={get(buttonSizeVariants, [sizeVariant, "fontSize"]) || buttonSizeVariants.medium.fontSize}
                    mergeCss={mergeCss}
                    {...typographySpreadProps}
                >
                    {/* Note: if passing an icon into the button, it will only receive button styles
                    if passed as a `ButtonIcon` */}
                    {children}
                </Typography>
            ) : (
                children
            )}
            {position === "right" || icon?.both ? (
                <ButtonIcon
                    src={icon?.rightIcon ?? src}
                    size={buttonSizeVariants[sizeVariant].icon}
                    css={css`
                        margin-left: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                    `}
                />
            ) : null}
            {/* Store Custom Code - End */}
            {/* Starter Custom Code */}
            {isLoading && <LoadingSpinner {...spinnerProps} />}
            {/* Starter Custom Code */}
        </ButtonWrapper>
    );
};

Button.displayName = "Button";
/** @component */
export default withDisabler(withTheme(Button));

export { ButtonIcon, ButtonWrapper };
